import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Camera, User, Phone, FileText, Save, Loader2, X, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Profile() {
  const { token, user: authUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [profile, setProfile] = useState({
    full_name: '',
    email: '',
    phone: '',
    notes: '',
    photo_url: '',
  });
  
  const [originalProfile, setOriginalProfile] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // Load profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await fetch(`${API}/api/auth/me/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setProfile({
            full_name: data.full_name || '',
            email: data.email || '',
            phone: data.phone || '',
            notes: data.notes || '',
            photo_url: data.photo_url || '',
          });
          setOriginalProfile({
            full_name: data.full_name || '',
            phone: data.phone || '',
            notes: data.notes || '',
            photo_url: data.photo_url || '',
          });
        }
      } catch (err) {
        toast.error('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [token]);

  // Track changes
  useEffect(() => {
    const changed = 
      profile.full_name !== originalProfile.full_name ||
      profile.phone !== originalProfile.phone ||
      profile.notes !== originalProfile.notes;
    setHasChanges(changed);
  }, [profile, originalProfile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/auth/me/profile`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({
          full_name: profile.full_name,
          phone: profile.phone,
          notes: profile.notes,
        })
      });
      
      if (res.ok) {
        toast.success('Profile updated!');
        setOriginalProfile({
          ...originalProfile,
          full_name: profile.full_name,
          phone: profile.phone,
          notes: profile.notes,
        });
        setHasChanges(false);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch (err) {
      toast.error('Network error');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Photo must be under 2MB');
      return;
    }
    
    // Validate type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Only JPG, PNG, WEBP allowed');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${API}/api/auth/me/upload-photo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      
      if (res.ok) {
        const data = await res.json();
        setProfile(p => ({ ...p, photo_url: data.photo_url }));
        setOriginalProfile(p => ({ ...p, photo_url: data.photo_url }));
        toast.success('Photo uploaded!');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.message || 'Network error - check your connection');
    } finally {
      setUploading(false);
    }
  };

  const goBack = () => {
    const backPath = authUser?.role === 'customer' ? '/portal/dashboard' : '/ops/dashboard';
    navigate(backPath);
  };

  const initials = profile.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'U';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <Loader2 size={32} className="animate-spin" style={{ color: 'var(--primary)' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl border-b"
        style={{ background: 'rgba(10, 13, 16, 0.97)', borderColor: 'var(--border)' }}>
        <div className="px-4 h-14 flex items-center justify-between max-w-lg mx-auto">
          <button onClick={goBack} className="p-2 -ml-2 rounded-md transition-colors"
            style={{ color: 'var(--text-muted)' }} data-testid="profile-back-btn">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-['Manrope'] font-bold text-base" style={{ color: 'var(--text-primary)' }}>
            Edit Profile
          </h1>
          <div className="w-9" /> {/* Spacer */}
        </div>
      </header>

      <div className="px-4 py-6 max-w-lg mx-auto space-y-6">
        {/* Photo Section */}
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            {profile.photo_url ? (
              <img 
                src={profile.photo_url} 
                alt="Profile"
                className="w-24 h-24 rounded-full object-cover"
                style={{ border: '3px solid var(--primary)' }}
              />
            ) : (
              <div className="w-24 h-24 rounded-full flex items-center justify-center"
                style={{ background: 'var(--primary-muted)', border: '3px solid var(--primary)' }}>
                <span className="text-2xl font-extrabold font-['Manrope']" style={{ color: 'var(--primary)' }}>
                  {initials}
                </span>
              </div>
            )}
            <button 
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full flex items-center justify-center transition-all"
              style={{ background: 'var(--primary)', color: 'white' }}
              data-testid="upload-photo-btn">
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
            </button>
            <input 
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handlePhotoUpload}
              className="hidden"
            />
          </div>
          <p className="text-xs" style={{ color: 'var(--text-faint)' }}>
            Tap camera to upload (max 2MB)
          </p>
        </div>

        {/* Form Fields */}
        <div className="space-y-4">
          {/* Full Name */}
          <div>
            <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider mb-2"
              style={{ color: 'var(--text-muted)' }}>
              <User size={14} /> Full Name
            </label>
            <input
              type="text"
              value={profile.full_name}
              onChange={e => setProfile(p => ({ ...p, full_name: e.target.value }))}
              placeholder="Your name"
              className="w-full input-slate"
              data-testid="profile-name-input"
            />
          </div>

          {/* Email (read-only) */}
          <div>
            <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider mb-2"
              style={{ color: 'var(--text-muted)' }}>
              Email
            </label>
            <input
              type="email"
              value={profile.email}
              disabled
              className="w-full input-slate opacity-60"
            />
            <p className="text-xs mt-1" style={{ color: 'var(--text-faint)' }}>
              Contact admin to change email
            </p>
          </div>

          {/* Phone */}
          <div>
            <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider mb-2"
              style={{ color: 'var(--text-muted)' }}>
              <Phone size={14} /> Phone Number
            </label>
            <input
              type="tel"
              value={profile.phone}
              onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))}
              placeholder="+1 (555) 123-4567"
              className="w-full input-slate"
              data-testid="profile-phone-input"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider mb-2"
              style={{ color: 'var(--text-muted)' }}>
              <FileText size={14} /> Notes
            </label>
            <textarea
              value={profile.notes}
              onChange={e => setProfile(p => ({ ...p, notes: e.target.value }))}
              placeholder="Personal notes..."
              rows={4}
              className="w-full input-slate resize-none"
              data-testid="profile-notes-input"
            />
          </div>
        </div>

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className="w-full btn-primary flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-wider disabled:opacity-50"
          data-testid="profile-save-btn">
          {saving ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Saving...
            </>
          ) : hasChanges ? (
            <>
              <Save size={16} />
              Save Changes
            </>
          ) : (
            <>
              <Check size={16} />
              No Changes
            </>
          )}
        </button>
      </div>
    </div>
  );
}
